// frontend/src/pages/SucursalesPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Página completa para gestión de sucursales del dueño/admin.
// Conecta con: GET/POST/PUT /api/sucursales
// Respeta el límite del plan (limite_sucursales) para bloquear la creación.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { sucursalesAPI, businessAPI } from '../services/api';
import './SucursalesPage.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserInfo = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
};

const SUCURSAL_MODULE_LABELS = {
    ventas:     '💰 Ventas',
    productos:  '📦 Productos',
    clientes:   '👥 Clientes',
    fiados:     '💳 Cuentas por Cobrar',
    inventario: '📋 Inventario',
    pedidos:    '📦 Pedidos',
    gastos:     '💸 Gastos',
    reportes:   '📈 Reportes',
    cronograma: '📅 Cronograma',
};

// ── Componente principal ──────────────────────────────────────────────────────
const SucursalesPage = () => {
    const [sucursales, setSucursales]   = useState([]);
    const [loading, setLoading]         = useState(true);
    const [isSaving, setIsSaving]       = useState(false);
    const [toast, setToast]             = useState(null);

    // Modal crear / editar
    const [showModal, setShowModal]     = useState(false);
    const [editTarget, setEditTarget]   = useState(null); // null = crear, obj = editar
    const [regenerandoId, setRegenerandoId] = useState(null);
    const [cajaLoadingId, setCajaLoadingId] = useState(null); // id de la sucursal abriendo/cerrando caja

    // Seguridad: codigo_acceso oculto por defecto — igual que una API key.
    // codigosVisibles = Set de ids cuyo código está actualmente revelado.
    // copiadoId       = id cuyo código acaba de copiarse (muestra flash "¡Copiado!").
    const [codigosVisibles, setCodigosVisibles] = useState(new Set());
    const [copiadoId,       setCopiadoId]       = useState(null);

    const toggleVisibilidad = (id) => {
        setCodigosVisibles(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const copiarCodigo = async (id, codigo) => {
        try {
            await navigator.clipboard.writeText(codigo);
            setCopiadoId(id);
            setTimeout(() => setCopiadoId(null), 2000);
        } catch {
            showToast('No se pudo copiar. Cópialo manualmente.', 'error');
        }
    };

    const emptyForm = { nombre: '', direccion: '', es_offline: true };
    const [form, setForm] = useState(emptyForm);

    // Límite del plan (viene del perfil guardado en localStorage)
    const user = getUserInfo();
    const limitePlan = user?.limite_sucursales ?? 1;

    // ── Data ─────────────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await sucursalesAPI.getAll();
            setSucursales(res.data?.data?.sucursales || []);
        } catch (e) {
            showToast('Error al cargar sucursales', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Toast ─────────────────────────────────────────────────────────────────
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    // ── Plan quota ────────────────────────────────────────────────────────────
    const activas    = sucursales.filter(s => s.activo).length;
    const porcentaje = limitePlan > 0 ? Math.min((activas / limitePlan) * 100, 100) : 100;
    const planClass  = porcentaje >= 100 ? 'danger' : porcentaje >= 80 ? 'warning' : 'ok';
    const atLimit    = activas >= limitePlan;

    // ── Modal helpers ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setEditTarget(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (s) => {
        setEditTarget(s);

        // Módulos: solo se ofrecen los módulos operativos que el Dueño tiene
        // habilitados en su cuenta (excluyendo módulos de administración global como sucursales y cajeros).
        const userModules = user?.modules || {};
        const sucModules  = s.modules || {};
        const modulesForm = {};
        Object.keys(SUCURSAL_MODULE_LABELS).forEach(key => {
            const hasModule = key === 'fiados' ? (userModules.fiados ?? userModules.clientes) : userModules[key];
            if (hasModule) modulesForm[key] = sucModules[key] !== false;
        });

        setForm({ nombre: s.nombre || '', direccion: s.direccion || '', es_offline: !!s.es_offline, modules: modulesForm });
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setEditTarget(null); setForm(emptyForm); };

    // ── Guardar (crear o editar) ──────────────────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim()) return showToast('El nombre es requerido', 'error');

        setIsSaving(true);
        try {
            if (editTarget) {
                await sucursalesAPI.update(editTarget.id, form);
                showToast('✅ Sucursal actualizada correctamente');
                if (String(localStorage.getItem('sucursal_id')) === String(editTarget.id)) {
                    if (form.modules) {
                        localStorage.setItem('sucursal_modules', JSON.stringify(form.modules));
                    }
                    window.dispatchEvent(new Event('sucursal-changed'));
                }
            } else {
                await sucursalesAPI.create(form);
                showToast('✅ Sucursal creada correctamente');
            }
            closeModal();
            load();
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al guardar';
            showToast(`❌ ${msg}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Toggle activo/inactivo ────────────────────────────────────────────────
    const handleToggle = async (s) => {
        const accion = s.activo ? 'desactivar' : 'activar';
        if (!window.confirm(`¿Seguro que deseas ${accion} la sucursal "${s.nombre}"?`)) return;
        try {
            await sucursalesAPI.update(s.id, { ...s, activo: !s.activo });
            showToast(`Sucursal ${s.activo ? 'desactivada' : 'activada'}`);
            load();
        } catch (err) {
            showToast('Error al cambiar el estado', 'error');
        }
    };

    // ── Regenerar código de acceso (login Terminal/Kiosco) ────────────────────
    const handleRegenerarCodigo = async (s) => {
        if (!window.confirm(`¿Regenerar el código de acceso de "${s.nombre}"? El código anterior dejará de funcionar de inmediato.`)) return;
        setRegenerandoId(s.id);
        try {
            await sucursalesAPI.regenerarCodigo(s.id);
            showToast('✅ Código de acceso regenerado');
            load();
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al regenerar el código';
            showToast(`❌ ${msg}`, 'error');
        } finally {
            setRegenerandoId(null);
        }
    };

    // ── Abrir / Cerrar Caja de la sucursal ──────────────────────────────────────
    const handleToggleCaja = async (s) => {
        const abrir = s.business_status !== 'abierto';
        setCajaLoadingId(s.id);
        try {
            const res = abrir ? await businessAPI.open(s.id) : await businessAPI.close(s.id);
            showToast(res.data?.message || (abrir ? '✅ Caja abierta' : '🔒 Caja cerrada'));
            load();
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al cambiar el estado de caja';
            showToast(`❌ ${msg}`, 'error');
        } finally {
            setCajaLoadingId(null);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="suc-page" style={{ width: '100%', maxWidth: '100%' }}>

            {/* Header */}
            <div className="suc-header">
                <div>
                    <h1>🏢 Mis Sucursales</h1>
                    <p>Gestiona los locales de tu negocio y su modo de operación</p>
                </div>
                <button
                    id="btn-nueva-sucursal"
                    className="suc-btn-new"
                    onClick={openCreate}
                    disabled={atLimit}
                    title={atLimit ? 'Has alcanzado el límite de tu plan actual' : 'Crear nueva sucursal'}
                >
                    ＋ Nueva Sucursal
                </button>
            </div>

            {/* Barra de plan */}
            <div className="suc-plan-bar">
                <div className="suc-plan-info">
                    <div className="suc-plan-title">📦 Plan contratado</div>
                    <div className="suc-plan-numbers">
                        {activas} <span>/ {limitePlan} sucursal{limitePlan !== 1 ? 'es' : ''}</span>
                    </div>
                </div>
                <div className="suc-plan-track">
                    <div className="suc-plan-title">Uso del plan</div>
                    <div className="suc-progress-bg">
                        <div
                            className={`suc-progress-fill ${planClass}`}
                            style={{ width: `${porcentaje}%` }}
                        />
                    </div>
                    <div className="suc-plan-label">
                        {porcentaje >= 100
                            ? '⚠️ Límite alcanzado — Contacta a soporte para ampliar'
                            : `${Math.round(porcentaje)}% utilizado — ${limitePlan - activas} disponible${(limitePlan - activas) !== 1 ? 's' : ''}`}
                    </div>
                </div>
            </div>

            {/* Alerta de límite */}
            {atLimit && (
                <div className="suc-limit-alert">
                    🔒 Has alcanzado el límite de tu plan actual. Para agregar más sucursales,{' '}
                    <a href="https://wa.me/51963152294" target="_blank" rel="noopener noreferrer"
                       style={{ color: '#25D366', fontWeight: 700, marginLeft: 4 }}>
                        📱 contacta a soporte por WhatsApp
                    </a>.
                </div>
            )}

            {/* Grid de tarjetas */}
            {loading ? (
                <div className="suc-loading">
                    <div className="suc-spinner" />
                    <span>Cargando sucursales...</span>
                </div>
            ) : (
                <div className="suc-grid">
                    {sucursales.length === 0 ? (
                        <div className="suc-empty">
                            <div className="suc-empty-icon">🏢</div>
                            <p>Aún no tienes sucursales registradas.<br/>¡Crea tu primera sucursal para comenzar!</p>
                        </div>
                    ) : (
                        sucursales.map(s => {
                            const modeClass = !s.activo ? 'inactive' : s.es_offline ? 'offline' : 'online';
                            return (
                                <div key={s.id} className={`suc-card ${modeClass}`}>
                                    <div className="suc-card-header">
                                        <div className="suc-card-name">{s.nombre}</div>
                                        <span className={`suc-badge ${modeClass}`}>
                                            {!s.activo  ? '⏸ Inactiva'  :
                                             s.es_offline ? '📴 Offline' : '🌐 Online'}
                                        </span>
                                    </div>

                                    {/* Estado de Caja — independiente por sucursal */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', borderRadius: 10,
                                        background: s.business_status === 'abierto' ? 'rgba(34,197,94,0.1)' : 'rgba(100,116,139,0.1)',
                                        border: `1px solid ${s.business_status === 'abierto' ? 'rgba(34,197,94,0.25)' : 'rgba(100,116,139,0.25)'}`,
                                    }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: s.business_status === 'abierto' ? '#4ade80' : '#94a3b8' }}>
                                            {s.business_status === 'abierto' ? '🟢 Caja Abierta' : '⚪ Caja Cerrada'}
                                        </span>
                                        <button
                                            onClick={() => handleToggleCaja(s)}
                                            disabled={cajaLoadingId === s.id || !s.activo}
                                            id={`btn-toggle-caja-${s.id}`}
                                            title={!s.activo ? 'La sucursal está inactiva' : undefined}
                                            style={{
                                                background: s.business_status === 'abierto' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                                                border: 'none', borderRadius: 6, cursor: !s.activo ? 'not-allowed' : 'pointer',
                                                fontSize: 11, fontWeight: 700, padding: '4px 10px',
                                                color: s.business_status === 'abierto' ? '#fca5a5' : '#4ade80',
                                                opacity: !s.activo ? 0.5 : 1,
                                            }}
                                        >
                                            {cajaLoadingId === s.id ? '...' : s.business_status === 'abierto' ? 'Cerrar' : 'Abrir'}
                                        </button>
                                    </div>

                                    <div className="suc-card-address">
                                        📍 {s.direccion || 'Sin dirección registrada'}
                                    </div>

                                    {s.productos_en_inventario > 0 && (
                                        <div style={{ fontSize: 12, color: '#64748b' }}>
                                            📦 {s.productos_en_inventario} producto{s.productos_en_inventario !== 1 ? 's' : ''} en inventario
                                        </div>
                                    )}

                                    <div className="suc-card-actions">
                                        <button
                                            className="suc-action-btn"
                                            onClick={() => openEdit(s)}
                                            id={`btn-edit-sucursal-${s.id}`}
                                        >
                                            ✏️ Editar
                                        </button>
                                        <button
                                            className={`suc-action-btn ${s.activo ? 'danger' : ''}`}
                                            onClick={() => handleToggle(s)}
                                            id={`btn-toggle-sucursal-${s.id}`}
                                        >
                                            {s.activo ? '⏸ Desactivar' : '▶️ Activar'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* ── MODAL Crear / Editar ────────────────────────────────────── */}
            {showModal && (
                <div className="suc-overlay" onClick={closeModal}>
                    <div className="suc-modal" onClick={e => e.stopPropagation()}>
                        <div className="suc-modal-header">
                            <h3>{editTarget ? '✏️ Editar Sucursal' : '🏢 Nueva Sucursal'}</h3>
                            <button className="suc-modal-close" onClick={closeModal}>✕</button>
                        </div>

                        <form onSubmit={handleSave}>
                            <div className="suc-form-group">
                                <label>Nombre de la sucursal *</label>
                                <input
                                    id="suc-input-nombre"
                                    required
                                    autoFocus
                                    value={form.nombre}
                                    onChange={e => setForm({ ...form, nombre: e.target.value })}
                                    placeholder="Ej: Alexis Tienda - Norte"
                                />
                            </div>

                            <div className="suc-form-group">
                                <label>Dirección</label>
                                <input
                                    id="suc-input-direccion"
                                    value={form.direccion}
                                    onChange={e => setForm({ ...form, direccion: e.target.value })}
                                    placeholder="Ej: Av. Lima 123, Cercado"
                                />
                            </div>

                            <div className="suc-form-group">
                                <div
                                    className="suc-switch-row"
                                    onClick={() => setForm({ ...form, es_offline: !form.es_offline })}
                                    id="suc-switch-offline"
                                >
                                    <div className="suc-switch-label">
                                        <strong>📴 Modo Offline habilitado</strong>
                                        <small>
                                            {form.es_offline
                                                ? 'Opera sin internet — ventas se sincronizan después'
                                                : 'Requiere conexión a internet para operar'}
                                        </small>
                                    </div>
                                    <div className={`suc-switch ${form.es_offline ? 'on' : ''}`} />
                                </div>
                            </div>

                            {/* Módulos por sucursal — solo al editar (no al crear) */}
                            {editTarget && (
                                <div className="suc-form-group">
                                    <label>📦 Módulos activos en esta sucursal</label>
                                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 10px' }}>
                                        Solo se muestran los módulos que ya tienes habilitados en tu cuenta.
                                        Desmarca los que no quieras usar en esta sucursal en particular — el
                                        catálogo general y el resto de tus sucursales no se ven afectados.
                                    </p>
                                    {Object.keys(form.modules || {}).length === 0 ? (
                                        <p style={{ fontSize: 13, color: '#64748b' }}>Tu cuenta no tiene módulos habilitados todavía.</p>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                                            {Object.keys(form.modules).map(key => (
                                                <label
                                                    key={key}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={!!form.modules[key]}
                                                        onChange={e => setForm({
                                                            ...form,
                                                            modules: { ...form.modules, [key]: e.target.checked }
                                                        })}
                                                    />
                                                    {SUCURSAL_MODULE_LABELS[key] || key}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="suc-form-actions">
                                <button type="button" className="suc-btn-cancel" onClick={closeModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="suc-btn-save" disabled={isSaving}>
                                    {isSaving ? '⏳ Guardando...' : editTarget ? '💾 Guardar cambios' : '✅ Crear Sucursal'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`suc-toast ${toast.type}`}>{toast.message}</div>
            )}
        </div>
    );
};

export default SucursalesPage;
